import actions from '@/store/modules/contactDirectory/actions'

/**
 * The tabs the address book is made of, in display order.
 *
 * Mirrors the `DirectoryTab` enum on the API side, which is also what the counts
 * route keys its answer by.
 *
 * @type {string[]}
 */
const DIRECTORY_TABS = ['messaging', 'dossiers', 'suppliers', 'internal']

/**
 * The paging envelope of a listing, before any request has answered.
 *
 * Seeded with the API's four keys rather than left null, because the first render
 * happens before the first fetch resolves and reads them: a null envelope turns
 * that render into a crash instead of an empty table.
 *
 * `per_page` mirrors the API's own default of 50. Every request built by
 * `buildListParams` sends its own value, so this figure only ever describes the
 * gap before the first answer.
 *
 * @returns {{current_page: number, last_page: number, per_page: number, total: number}}
 */
function emptyMeta() {
  return { current_page: 1, last_page: 1, per_page: 50, total: 0 }
}

/**
 * The four tab badges, before the counts route has answered.
 *
 * null means "not fetched yet", and cannot be mistaken for a figure the API sent:
 * the counts route answers every tab with two integers, the Internal tab included
 * — it reports zero from its own query rather than from a special case. Seeding
 * the keys is what keeps a badge reading `counts[tab].total` from throwing on the
 * first render.
 *
 * @returns {Record<string, {total: number|null, duplicates: number|null}>}
 */
function emptyCounts() {
  return Object.fromEntries(
    DIRECTORY_TABS.map((tab) => [tab, { total: null, duplicates: null }])
  )
}

export default {
  namespaced: true,
  state() {
    return {
      // The listing envelope as the API shapes it: `entries`, not `data`, and no
      // `links` — this route is not a Laravel resource collection, it builds its
      // own response.
      list: { entries: [], meta: emptyMeta(), sortable: [] },
      counts: emptyCounts(),
      // The token of the last merge preview, or null. Lives here and only
      // here — see fetchMergePreview's docblock — so that executeMerge always
      // fires the merge the user is looking at rather than one a stray copy
      // of the token remembers.
      mergeToken: null
    }
  },
  getters: {
    /**
     * The rows of the current page, exactly as the API sent them.
     *
     * Nothing is coalesced here, and nothing may be: `invoice_count` and
     * `dossier_count` are null when the tab cannot count them and 0 when it
     * counted none, and the two render differently — a dash against a number.
     * Any `?? 0`, `|| 0` or filtering of null keys added to this getter would
     * turn "this tab cannot answer" into "this row has none".
     */
    getDirectoryRows: (state) => state.list.entries,
    getDirectoryMeta: (state) => state.list.meta,
    /**
     * The sort keys the API accepts on the tab that was last listed.
     *
     * This is the API's own answer. The column definitions deliberately derive
     * their sort controls from the local mirror in `directoryTable.js` instead,
     * so that the request builder and the headers cannot disagree; this getter
     * is that mirror's counterpart, not a second source to wire up beside it.
     */
    getDirectorySortable: (state) => state.list.sortable,
    getDirectoryCounts: (state) => state.counts
  },
  mutations: {
    /**
     * Store one listing answer, keeping the envelope's shape whatever arrives.
     *
     * The fallbacks guard the three top-level keys only. The rows inside
     * `entries` are stored untouched, so a null count stays null.
     */
    setDirectoryList(state, payload) {
      state.list = {
        entries: payload?.entries ?? [],
        meta: payload?.meta ?? emptyMeta(),
        sortable: payload?.sortable ?? []
      }
    },
    setDirectoryCounts(state, payload) {
      state.counts = payload ?? emptyCounts()
    },
    setMergeToken(state, token) {
      state.mergeToken = token
    }
  },
  actions
}
